/**
 * Mieterhöhung — reiner Rechenkern (kein I/O, CLAUDE.md Regel 5).
 *
 * Drei Wege, je nach Mietart:
 *   § 558  BGB  Erhöhung bis zur ortsüblichen Vergleichsmiete (Mietspiegel),
 *               begrenzt durch die Kappungsgrenze und Sperrfristen.
 *   § 557a BGB  Staffelmiete — feste Stufen zu festen Terminen, je Stufe mind. 1 Jahr.
 *               Kappungsgrenze und Vergleichsmiete gelten NICHT.
 *   § 557b BGB  Indexmiete — an den Verbraucherpreisindex gekoppelt, je Änderung mind. 1 Jahr.
 *
 * Rechtsstand geprüft 15.09.2026. Beträge sind Integer in Cent (Regel 6).
 * Jede Regel trägt ihren Bezug; Altfälle bleiben nachrechenbar (Regel 8).
 */

import { assertCent, type Cent } from './geld';
import { addiereMonate, type ISODate } from './zeit';

const RX = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Erster Tag des Monats. */
export function monatsanfang(d: ISODate): ISODate {
  const m = RX.exec(d);
  if (!m) throw new Error(`Ungültiges Datum: ${d} (erwartet YYYY-MM-DD)`);
  return `${m[1]}-${m[2]}-01`;
}

/** a < b ? (lexikografischer ISO-Vergleich ist datumskorrekt) */
function vor(a: ISODate, b: ISODate): boolean {
  return a < b;
}

function spaeteres(a: ISODate, b: ISODate): ISODate {
  return a >= b ? a : b;
}

// ------------------------------------------------------------------ § 558 BGB

export type Begrenzung = 'vergleichsmiete' | 'kappungsgrenze' | 'bereits_erreicht';

export interface Eingabe558 {
  aktuelleKaltmieteCent: Cent;
  wohnflaecheQm: number;
  /** Ortsübliche Vergleichsmiete je m² und Monat, in Cent (Mietspiegel). */
  vergleichsmieteQmCent: Cent;
  /** 20 % Regelfall, 15 % in Gebieten mit angespanntem Wohnungsmarkt (§ 558 Abs. 3 S. 2/3). */
  kappungsgrenzeProzent: 15 | 20;
  /** Stichtag der letzten Mieterhöhung; fehlt sie, gilt der Mietbeginn. */
  letzteAenderungAm: ISODate;
  /** Tag, an dem das Erhöhungsverlangen zugehen soll (i. d. R. heute). */
  stichtag: ISODate;
}

export interface Ergebnis558 {
  aktuelleKaltmieteCent: Cent;
  /** Vergleichsmiete × Wohnfläche, gerundet. */
  vergleichsmieteGesamtCent: Cent;
  /** Aktuelle Miete + Kappungsspielraum, gerundet. */
  kappungsobergrenzeCent: Cent;
  /** Höchste rechtlich zulässige neue Kaltmiete zum Stichtag. */
  neueKaltmieteCent: Cent;
  erhoehungCent: Cent;
  erhoehungProzent: number; // eine Nachkommastelle
  begrenztDurch: Begrenzung;
  /** Ist-Miete je m² und Monat (Cent) — für den Mietspiegel-Vergleich. */
  istQmCent: Cent;
  /** Abweichung der Ist-Miete von der Vergleichsmiete in Prozent (negativ = darunter). */
  abweichungQmProzent: number;
  /** Erhöhungsverlangen frühestens ab: letzte Änderung + 12 Monate (Jahressperre). */
  fruehestesVerlangenAm: ISODate;
  /** Neue Miete frühestens geschuldet ab (§ 558 Abs. 1: 15 Monate seit letzter Änderung;
   *  § 558b: Beginn des 3. Kalendermonats nach Zugang des Verlangens — das spätere gilt). */
  wirksamAbFruehestensAm: ISODate;
  /** true, wenn zum Stichtag noch nicht einmal das Verlangen gestellt werden darf. */
  sperrfristLaeuft: boolean;
}

function runde(n: number): Cent {
  return Math.round(n);
}

/**
 * § 558 BGB — Erhöhung bis zur ortsüblichen Vergleichsmiete.
 *
 * Zulässig ist der niedrigere der beiden Deckel: die Vergleichsmiete (Mietspiegel × Fläche)
 * und die Kappungsobergrenze (aktuelle Miete + 15/20 % in drei Jahren). Liegt die Ist-Miete
 * bereits auf oder über der Vergleichsmiete, ist keine Erhöhung möglich.
 */
export function mieterhoehung558(e: Eingabe558): Ergebnis558 {
  assertCent(e.aktuelleKaltmieteCent, 'Kaltmiete');
  assertCent(e.vergleichsmieteQmCent, 'Vergleichsmiete/m²');
  if (e.wohnflaecheQm <= 0) throw new Error('Wohnfläche muss größer als 0 sein');
  if (e.kappungsgrenzeProzent !== 15 && e.kappungsgrenzeProzent !== 20) {
    throw new Error('Kappungsgrenze ist 15 oder 20 Prozent');
  }

  const vergleichsmieteGesamt = runde(e.vergleichsmieteQmCent * e.wohnflaecheQm);
  const kappungsobergrenze = runde(e.aktuelleKaltmieteCent * (1 + e.kappungsgrenzeProzent / 100));

  const zulaessigRoh = Math.min(vergleichsmieteGesamt, kappungsobergrenze);
  const neueKaltmiete = Math.max(e.aktuelleKaltmieteCent, zulaessigRoh);
  const erhoehung = neueKaltmiete - e.aktuelleKaltmieteCent;

  let begrenztDurch: Begrenzung;
  if (vergleichsmieteGesamt <= e.aktuelleKaltmieteCent) {
    begrenztDurch = 'bereits_erreicht';
  } else if (kappungsobergrenze < vergleichsmieteGesamt) {
    begrenztDurch = 'kappungsgrenze';
  } else {
    begrenztDurch = 'vergleichsmiete';
  }

  const istQm = runde(e.aktuelleKaltmieteCent / e.wohnflaecheQm);
  const abweichungQmProzent =
    e.vergleichsmieteQmCent === 0
      ? 0
      : Math.round(((istQm - e.vergleichsmieteQmCent) / e.vergleichsmieteQmCent) * 1000) / 10;

  const fruehestesVerlangen = addiereMonate(e.letzteAenderungAm, 12);
  const wirksam15 = addiereMonate(e.letzteAenderungAm, 15);
  // Zugang zum Stichtag → Zustimmungsfrist 2 volle Monate → geschuldet ab Beginn des 3. Monats.
  const wirksamNachZugang = monatsanfang(addiereMonate(e.stichtag, 3));
  const wirksamAbFruehestens = spaeteres(wirksam15, wirksamNachZugang);

  return {
    aktuelleKaltmieteCent: e.aktuelleKaltmieteCent,
    vergleichsmieteGesamtCent: vergleichsmieteGesamt,
    kappungsobergrenzeCent: kappungsobergrenze,
    neueKaltmieteCent: neueKaltmiete,
    erhoehungCent: erhoehung,
    erhoehungProzent:
      e.aktuelleKaltmieteCent === 0
        ? 0
        : Math.round((erhoehung / e.aktuelleKaltmieteCent) * 1000) / 10,
    begrenztDurch,
    istQmCent: istQm,
    abweichungQmProzent,
    fruehestesVerlangenAm: fruehestesVerlangen,
    wirksamAbFruehestensAm: wirksamAbFruehestens,
    sperrfristLaeuft: vor(e.stichtag, fruehestesVerlangen),
  };
}

// ------------------------------------------------------------------ § 557b BGB Index

export interface EingabeIndex {
  aktuelleKaltmieteCent: Cent;
  /** Verbraucherpreisindex bei der letzten Festsetzung (Basis). */
  basisIndex: number;
  /** Aktueller Verbraucherpreisindex. */
  aktuellerIndex: number;
  letzteAenderungAm: ISODate;
  stichtag: ISODate;
}

export interface ErgebnisIndex {
  neueKaltmieteCent: Cent;
  erhoehungCent: Cent;
  erhoehungProzent: number;
  /** § 557b Abs. 2: die Miete muss jeweils mind. 1 Jahr unverändert gewesen sein. */
  fruehestensAm: ISODate;
  sperrfristLaeuft: boolean;
}

/**
 * § 557b BGB — Indexmiete. Die neue Miete folgt proportional der Indexänderung.
 * Kappungsgrenze und Vergleichsmiete gelten nicht; nur die Jahresfrist je Änderung.
 */
export function indexmiete557b(e: EingabeIndex): ErgebnisIndex {
  assertCent(e.aktuelleKaltmieteCent, 'Kaltmiete');
  if (e.basisIndex <= 0) throw new Error('Basisindex muss größer als 0 sein');
  if (e.aktuellerIndex <= 0) throw new Error('Aktueller Index muss größer als 0 sein');

  const neue = runde((e.aktuelleKaltmieteCent * e.aktuellerIndex) / e.basisIndex);
  const neueKaltmiete = Math.max(e.aktuelleKaltmieteCent, neue);
  const erhoehung = neueKaltmiete - e.aktuelleKaltmieteCent;
  const fruehestens = addiereMonate(e.letzteAenderungAm, 12);

  return {
    neueKaltmieteCent: neueKaltmiete,
    erhoehungCent: erhoehung,
    erhoehungProzent:
      e.aktuelleKaltmieteCent === 0
        ? 0
        : Math.round((erhoehung / e.aktuelleKaltmieteCent) * 1000) / 10,
    fruehestensAm: fruehestens,
    sperrfristLaeuft: vor(e.stichtag, fruehestens),
  };
}

// ------------------------------------------------------------------ § 557a BGB Staffel

export interface Staffel {
  ab: ISODate;
  kaltmieteCent: Cent;
}

export interface StaffelStand {
  /** Aktuell geschuldete Stufe zum Stichtag (oder null vor der ersten Stufe). */
  aktuell: Staffel | null;
  /** Nächste Stufe nach dem Stichtag (oder null, wenn keine folgt). */
  naechste: Staffel | null;
  /** true, wenn jede Stufe mind. 12 Monate trägt (§ 557a Abs. 2 S. 1). */
  laufzeitenGueltig: boolean;
}

/**
 * § 557a BGB — Staffelmiete. Die Stufen stehen im Vertrag; hier wird nur der Stand
 * zum Stichtag bestimmt und geprüft, dass jede Stufe mindestens ein Jahr unverändert
 * bleibt (Abs. 2 S. 1). Die Erhöhung selbst ist bereits vereinbart, nicht neu zu verlangen.
 */
export function staffelStand(stufen: Staffel[], stichtag: ISODate): StaffelStand {
  const sortiert = [...stufen].sort((a, b) => (a.ab < b.ab ? -1 : a.ab > b.ab ? 1 : 0));

  let laufzeitenGueltig = true;
  for (let i = 1; i < sortiert.length; i++) {
    if (vor(sortiert[i].ab, addiereMonate(sortiert[i - 1].ab, 12))) {
      laufzeitenGueltig = false;
      break;
    }
  }

  let aktuell: Staffel | null = null;
  let naechste: Staffel | null = null;
  for (const s of sortiert) {
    if (!vor(stichtag, s.ab)) {
      aktuell = s; // s.ab <= stichtag
    } else if (naechste === null) {
      naechste = s; // erste Stufe nach dem Stichtag
    }
  }

  return { aktuell, naechste, laufzeitenGueltig };
}
